import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Chip,
  Autocomplete,
  Typography,
  Alert,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { referenceApi, api, mediaTypeApi, autoCleanupMediaTypes } from '../services/api';

const CampaignDialog = ({ open, onClose, onSave, initialData }) => {
  const [brands, setBrands] = useState([]);
  const [subBrands, setSubBrands] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [campaignStatuses, setCampaignStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Form data - match all fields with database structure
  const [formData, setFormData] = useState({
    campaign_name: '',
    brand_id: '',
    sub_brand_id: '',
    objective: '',
    start_date: null,
    end_date: null,
    
    media_types: [],
    // Additional fields from database
    campaign_naming_taxonomy: '',
    campaign_type: '',
    agency_id: '',
    planning_total: '',
    actual_total: '',
    saving_total: '',
    unused_total: ''
  });

  // State for reference data
  const [agencies, setAgencies] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);

  // Load all reference data needed for dropdowns
  useEffect(() => {
    if (open) {
      // Reset errors when dialog opens
      setError(null);
      setValidationErrors({});
      
      // Fetch all reference data
      fetchReferenceData();
      
      // If we're editing, populate the form
      if (initialData) {
        // Chắc chắn rằng brand_id và sub_brand_id được đặt đúng format
        const brandId = initialData.brand_id || initialData.brand || '';
        const subBrandId = initialData.sub_brand_id || initialData.sub_brand || '';
        
        setFormData({
          campaign_name: initialData.campaign_name || '',
          brand_id: brandId,
          sub_brand_id: subBrandId,
          objective: initialData.objective || '',
          start_date: initialData.start_date ? new Date(initialData.start_date) : null,
          end_date: initialData.end_date ? new Date(initialData.end_date) : null,
          campaign_status: initialData.campaign_status || 'planning',
          media_types: initialData.media_types ? 
            // Group media types by name to remove duplicates
            [...new Set(initialData.media_types.map(mt => mt.media_type))] : [],
          // Preserve all financial data
          total_given: initialData.total_given || initialData.given_lc || initialData.given_cost || '',
          planning_total: initialData.planning_total || initialData.planning_gross_cost || initialData.planning_cost || '',
          actual_total: initialData.actual_total || initialData.actual_gross_cost || initialData.actual_cost || '',
          saving_total: initialData.saving_total || '',
          unused_total: initialData.unused_total || '',
          // Additional fields
          campaign_naming_taxonomy: initialData.campaign_naming_taxonomy || '',
          campaign_type: initialData.campaign_type || '',
          agency_id: initialData.agency_id || ''
        });
        
        // Fetch sub-brands for the selected brand
        if (brandId) {
          console.log(`Fetching sub-brands for brand ID: ${brandId} in edit mode`);
          fetchSubBrands(brandId);
        }
      } else {
        // Reset form for new campaign
        setFormData({
          campaign_name: '',
          brand_id: '',
          sub_brand_id: '',
          objective: '',
          start_date: null,
          end_date: null,
    
          media_types: [],
          // Additional fields
          campaign_naming_taxonomy: '',
          campaign_type: '',
          agency_id: '',
          planning_total: '',
          actual_total: '',
          saving_total: '',
          unused_total: ''
        });
        setSubBrands([]);
      }
    }
  }, [open, initialData]);

  // Fetch all reference data
  const fetchReferenceData = async () => {
    setLoading(true);
    try {
      // Fetch brands
      await fetchBrands();
      
      // Fetch media types and statuses
      await fetchMediaTypesAndStatuses();
      
      // Fetch additional reference data
      await fetchAgenciesAndCampaignTypes();
    } catch (err) {
      console.error('Error fetching reference data:', err);
      setError('Error loading reference data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch agencies and campaign types
  const fetchAgenciesAndCampaignTypes = async () => {
    try {
      // Fetch agencies
      const agenciesResponse = await referenceApi.getAgencies();
      console.log('Agencies response:', agenciesResponse);
      
      if (agenciesResponse.data && Array.isArray(agenciesResponse.data)) {
        setAgencies(agenciesResponse.data.map(agency => ({
          id: agency.agency_id || agency.id,
          name: agency.agency_name || agency.name
        })));
      } else {
        setAgencies([]);
      }
      
      // Fetch campaign types
      const campaignTypesResponse = await referenceApi.getCampaignTypes();
      console.log('Campaign types response:', campaignTypesResponse);
      
      if (campaignTypesResponse.data && Array.isArray(campaignTypesResponse.data)) {
        setCampaignTypes(campaignTypesResponse.data.map(type => ({
          id: type.campaign_type_id || type.id,
          name: type.campaign_type_name || type.name
        })));
      } else {
        setCampaignTypes([]);
      }
    } catch (err) {
      console.error('Error fetching additional reference data:', err);
      setAgencies([]);
      setCampaignTypes([]);
    }
  };

  // Fetch brands directly from brands API
  const fetchBrands = async () => {
    try {
      console.log('Fetching brands from category/brands endpoint');
      const response = await referenceApi.getBrands();
      console.log('Brands API response:', response.data);
      
      // Set brands from the API response
      setBrands(response.data || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError('Failed to load brands. Please try again.');
      setBrands([]);
    }
  };

  // Fetch media types and statuses
  const fetchMediaTypesAndStatuses = async () => {
    try {
      // Get media types directly from the API
      const mediaTypesResponse = await referenceApi.getMediaTypes();
      console.log('Media types response:', mediaTypesResponse);
      
      if (mediaTypesResponse.data && Array.isArray(mediaTypesResponse.data)) {
        // Map media types to a string array for the Autocomplete component
        setMediaTypes(mediaTypesResponse.data.map(type => 
          type.media_type_channel_name || type.name || String(type)
        ));
      } else {
        setMediaTypes([]);
      }
      
      // Get statuses directly from the API
      const statusesResponse = await referenceApi.getStatuses();
      console.log('Statuses response:', statusesResponse);
      
      if (statusesResponse.data && Array.isArray(statusesResponse.data)) {
        setCampaignStatuses(statusesResponse.data.map(status => ({
          id: status.status_code || status.code || status.id,
          name: status.status_name || status.name
        })));
      } else {
        setCampaignStatuses([]);
      }
    } catch (err) {
      console.error('Error fetching media types or statuses:', err);
      setMediaTypes([]);
      setCampaignStatuses([]);
    }
  };

  // Fetch sub-brands for a specific brand
  const fetchSubBrands = async (brandId) => {
    if (!brandId) {
      setSubBrands([]);
      return;
    }
    
    setLoading(true);
    try {
      console.log(`Fetching sub-brands for brand ID: ${brandId}`);
      const response = await referenceApi.getSubBrands(brandId);
      console.log('Sub-brands API response:', response.data);
      
      // Process the sub-brands data
      const subBrandsData = response.data || [];
      
      // Map sub-brand data to a consistent format
      setSubBrands(subBrandsData.map(subBrand => ({
        id: subBrand.sub_brand_id || subBrand.id,
        sub_brand_id: subBrand.sub_brand_id || subBrand.id,
        sub_brand_name: subBrand.sub_brand_name || subBrand.name,
        brand_id: subBrand.brand_id || brandId
      })));
      
      // Log sub-brands for debugging
      console.log(`Loaded ${subBrandsData.length} sub-brands for brand ${brandId}`);
      
      // Ensure the current sub_brand_id is valid for this brand
      // If not in editing mode or if the sub_brand_id doesn't belong to this brand, reset it
      const formSubBrandId = formData.sub_brand_id;
      if (formSubBrandId) {
        const isValidSubBrand = subBrandsData.some(sb => 
          (sb.sub_brand_id || sb.id) == formSubBrandId);
        
        if (!isValidSubBrand) {
          console.log(`Selected sub-brand ID ${formSubBrandId} is not valid for brand ${brandId}, clearing selection`);
          setFormData(prev => ({
            ...prev,
            sub_brand_id: ''
          }));
        } else {
          console.log(`Confirmed sub-brand ID ${formSubBrandId} is valid for brand ${brandId}`);
        }
      }
    } catch (err) {
      console.error('Error fetching sub-brands:', err);
      setError('Failed to load sub-brands. Please try again.');
      setSubBrands([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle brand change and load related sub-brands
  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    setFormData({
      ...formData,
      brand_id: brandId,
      sub_brand_id: '' // Reset sub-brand when brand changes
    });
    fetchSubBrands(brandId);
    
    // Clear any brand validation error using the correct field name
    if (validationErrors.brand) {
      setValidationErrors({
        ...validationErrors,
        brand: null
      });
    }
  };

  // Handle form input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error for this field if it exists
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  // Handle date changes
  const handleDateChange = (name, date) => {
    setFormData({
      ...formData,
      [name]: date
    });
    
    // Clear validation error for this field if it exists
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  // Handle media types selection
  const handleMediaTypesChange = (event, newValue) => {
    console.log("Media types changed to:", newValue);
    
    // Đảm bảo rằng danh sách media types mới được áp dụng đúng
    // Và loại bỏ bất kỳ tham chiếu cũ nào
    setFormData({
      ...formData,
      media_types: [...newValue] // Tạo array mới để đảm bảo state được cập nhật
    });
    
    // Clear media_types validation error if it exists
    if (validationErrors.media_types) {
      setValidationErrors({
        ...validationErrors,
        media_types: null
      });
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    // Required fields based on backend requirements
    if (!formData.campaign_name) errors.campaign_name = 'Campaign name is required';
    if (!formData.brand_id) errors.brand = 'Brand is required';
    if (!formData.media_types.length) errors.media_types = 'At least one media type is required';
    
    // Frontend-only validation (not required by backend but good UX)
    // Note: These are marked as warnings but won't block submission
    if (!formData.start_date) {
      console.warn('Start date is not provided, but it will be submitted as null');
    }
    
    if (!formData.end_date) {
      console.warn('End date is not provided, but it will be submitted as null');
    }
    
    // Validate dates if both are provided
    if (formData.start_date && formData.end_date && 
        formData.start_date > formData.end_date) {
      errors.end_date = 'End date must be after start date';
    }
    
    // Update validation errors state
    setValidationErrors(errors);
    
    // Form is valid if there are no errors
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);

    // Format data for the API request
    const formattedData = {
      // Required fields
      campaign_name: formData.campaign_name.trim(),
      brand: parseInt(formData.brand_id, 10), // Use 'brand' field as expected by backend

      
      // Optional fields - pass only if they have a value
      ...(formData.sub_brand_id && { sub_brand: parseInt(formData.sub_brand_id, 10) }),
      ...(formData.objective && { objective: formData.objective }),
      ...(formData.start_date && { 
        start_date: formData.start_date instanceof Date ? 
          formData.start_date.toISOString().split('T')[0] : formData.start_date 
      }),
      ...(formData.end_date && { 
        end_date: formData.end_date instanceof Date ? 
          formData.end_date.toISOString().split('T')[0] : formData.end_date 
      }),
      
      // Convert array of media types to proper format for backend
      media_types_input: (() => {
        console.log('Original formData.media_types:', formData.media_types);
        return (formData.media_types || []).map(type => {
          console.log('Processing media type:', type, 'Type:', typeof type);
        // If type is a string, create an object with media_type property
        if (typeof type === 'string') {
          return { media_type: type.trim() };
        }
        
        // If type is already an object, extract the key information and create a new object
        if (typeof type === 'object' && type !== null) {
          // Try to extract the name from various possible formats
          const mediaTypeName = type.name || type.media_type || type.media_type_channel_name;
          
          // If mediaTypeName is itself an object, try to extract a string from it
          if (typeof mediaTypeName === 'object' && mediaTypeName !== null) {
            const extractedName = mediaTypeName.name || mediaTypeName.media_type || 
                              mediaTypeName.media_type_channel_name || '';
            
            return { 
              media_type: typeof extractedName === 'string' ? extractedName.trim() : 'Digital'
            };
          }
          
          return { 
            media_type: typeof mediaTypeName === 'string' ? mediaTypeName.trim() : 'Digital'
          };
        }
        
        // Fallback
        return { media_type: 'Digital' };
        });
      })(),
      
      // Preserve financial data from form 
      ...(formData.total_given !== undefined && formData.total_given !== '' && { total_given: formData.total_given }),
      ...(formData.planning_total !== undefined && formData.planning_total !== '' && { planning_total: formData.planning_total }),
      ...(formData.actual_total !== undefined && formData.actual_total !== '' && { actual_total: formData.actual_total }),
      ...(formData.saving_total !== undefined && formData.saving_total !== '' && { saving_total: formData.saving_total }),
      ...(formData.unused_total !== undefined && formData.unused_total !== '' && { unused_total: formData.unused_total }),
      
      // Additional fields from database
      ...(formData.campaign_type && { campaign_type: formData.campaign_type }),
    };

    console.log('Submitting campaign data to backend:', formattedData);

    // --- AUTO CLEANUP MEDIA TYPES TRƯỚC KHI LƯU ---
    // TEMPORARILY DISABLED TO PREVENT DATA LOSS
    /*
    if (initialData && initialData.id) {
      try {
        const cleanupResult = await autoCleanupMediaTypes(initialData.id, formattedData.media_types);
        console.log('[AUTO] Cleanup media types result:', cleanupResult);
      } catch (cleanupError) {
        console.error('[AUTO] Cleanup media types error:', cleanupError);
      }
    }
    */
    // --- END AUTO CLEANUP ---

    // Submit data to backend via the campaigns service through the onSave callback
    onSave(formattedData)
      .then(async (response) => {
        setLoading(false);
        
        // Log response để debug
        console.log('Campaign created/updated successfully:', response);
        console.log('Response data:', response && response.data ? JSON.stringify(response.data) : 'No data');
        
        // Lấy campaign ID từ response một cách chính xác
        let campaignId;
        if (response && response.data && response.data.id) {
          // Trường hợp response có data.id
          campaignId = response.data.id;
        } else if (response && response.id) {
          // Trường hợp response có id trực tiếp
          campaignId = response.id;
        } else if (initialData && initialData.id) {
          // Trường hợp là edit campaign hiện có
          campaignId = initialData.id;
        }
        
        console.log('Campaign ID for media types:', campaignId);
        
        // Media types should already be created with the campaign
        console.log('Media types were sent with campaign creation, no additional creation needed');
        
        // Close dialog after all operations
        onClose();
      })
      .catch(error => {
        console.error('Error saving campaign:', error);
        if (error.response && error.response.data) {
          console.error('Server response:', error.response.data);
          
          // Map API errors to form fields for highlighting if available
          if (typeof error.response.data === 'object') {
            const newValidationErrors = {};
            
            Object.entries(error.response.data).forEach(([field, errors]) => {
              newValidationErrors[field] = Array.isArray(errors) ? errors.join(', ') : errors;
            });
            
            setValidationErrors(newValidationErrors);
          }
        }
        
        setLoading(false);
        setError('Failed to save campaign. Please try again.');
      });
  };

  // Check if a field has validation errors
  const hasError = (fieldName) => {
    return Boolean(validationErrors[fieldName]);
  };

  // Get error message for a field
  const getErrorMessage = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        fontWeight: 600,
        fontSize: '1.25rem',
        color: '#2e7d32'
      }}>
        {initialData ? 'Edit Campaign' : 'Create New Campaign'}
      </DialogTitle>
      
      <DialogContent 
        dividers={false}
        sx={{
          backgroundColor: 'white',
          p: 3
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </Alert>
        )}
        
        <Box component="form" noValidate sx={{ 
          mt: 2,
          '& .MuiTextField-root, & .MuiFormControl-root': {
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              fontSize: '0.9rem',
              height: '56px',
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.1)',
                borderWidth: 1,
              },
              '&:hover fieldset': {
                borderColor: 'rgba(46, 125, 50, 0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#2e7d32',
                borderWidth: 2,
              },
              '& input, & textarea': {
                fontSize: '0.9rem',
                fontWeight: 400,
                color: '#333',
              }
            },
            '& .MuiSelect-select': {
              fontSize: '0.9rem',
              fontWeight: 400,
              color: '#333',
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            fontWeight: 500,
            color: '#666',
            '&.Mui-focused': {
              color: '#2e7d32',
              fontWeight: 600,
            },
            '&.MuiInputLabel-shrink': {
              fontSize: '0.8rem',
            }
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.75rem',
            fontWeight: 400,
            marginTop: 1,
          },
          '& .MuiChip-root': {
            borderRadius: 1.5,
            backgroundColor: 'rgba(46, 125, 50, 0.08)',
            color: '#2e7d32',
            border: '1px solid rgba(46, 125, 50, 0.2)',
            fontSize: '0.8rem',
            fontWeight: 500,
            height: '28px',
          },
          '& .MuiAutocomplete-root': {
            '& .MuiOutlinedInput-root': {
              minHeight: '56px',
              height: 'auto',
              padding: '8px 12px',
            }
          }
        }}>
          <Grid container spacing={2.5}>
            {/* Brand Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={hasError('brand')}>
                <InputLabel id="brand-label">Brand</InputLabel>
                <Select
                  labelId="brand-label"
                  id="brand_id"
                  name="brand_id"
                  value={formData.brand_id}
                  onChange={handleBrandChange}
                  label="Brand"
                  disabled={loading}
                >
                  <MenuItem value="">Select a brand</MenuItem>
                  {Array.isArray(brands) && brands.length > 0 ? (
                    brands.map((brand) => (
                      <MenuItem 
                        key={brand.brand_id} 
                        value={brand.brand_id}
                      >
                        {brand.brand_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No brands available</MenuItem>
                  )}
                </Select>
                {hasError('brand') && (
                  <FormHelperText error>{getErrorMessage('brand')}</FormHelperText>
                )}
                {loading && <FormHelperText>Loading brands...</FormHelperText>}
              </FormControl>
            </Grid>
            
            {/* Sub-Brand Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={hasError('sub_brand')}>
                <InputLabel id="sub-brand-label">Sub-Brand</InputLabel>
                <Select
                  labelId="sub-brand-label"
                  id="sub_brand_id"
                  name="sub_brand_id"
                  value={formData.sub_brand_id}
                  onChange={handleChange}
                  label="Sub-Brand"
                  disabled={!formData.brand_id || loading || (Array.isArray(subBrands) && subBrands.length === 0)}
                >
                  <MenuItem value="">None</MenuItem>
                  {Array.isArray(subBrands) && subBrands.length > 0 ? (
                    subBrands.map((subBrand) => (
                      <MenuItem 
                        key={subBrand.sub_brand_id || subBrand.id} 
                        value={subBrand.sub_brand_id || subBrand.id}
                      >
                        {subBrand.sub_brand_name || subBrand.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No sub-brands available</MenuItem>
                  )}
                </Select>
                {hasError('sub_brand') && (
                  <FormHelperText error>{getErrorMessage('sub_brand')}</FormHelperText>
                )}
                {loading && <FormHelperText>Loading sub-brands...</FormHelperText>}
              </FormControl>
            </Grid>
            
            {/* Campaign Name */}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="campaign_name"
                name="campaign_name"
                label="Campaign Name"
                value={formData.campaign_name}
                onChange={handleChange}
                error={hasError('campaign_name')}
                helperText={getErrorMessage('campaign_name')}
              />
            </Grid>
            
            {/* Campaign Naming Taxonomy */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="campaign_naming_taxonomy"
                name="campaign_naming_taxonomy"
                label="Campaign Naming Taxonomy"
                value={formData.campaign_naming_taxonomy}
                onChange={handleChange}
                error={hasError('campaign_naming_taxonomy')}
                helperText={getErrorMessage('campaign_naming_taxonomy')}
              />
            </Grid>
            
            {/* Campaign Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={hasError('campaign_type')}>
                <InputLabel id="campaign-type-label">Campaign Type</InputLabel>
                <Select
                  labelId="campaign-type-label"
                  id="campaign_type"
                  name="campaign_type"
                  value={formData.campaign_type}
                  onChange={handleChange}
                  label="Campaign Type"
                  disabled={loading}
                >
                  <MenuItem value="">None</MenuItem>
                  {campaignTypes.map((type) => (
                    <MenuItem key={type.campaign_type_id || type.id} value={type.campaign_type_id || type.id}>
                      {type.campaign_type_name || type.name}
                    </MenuItem>
                  ))}
                </Select>
                {hasError('campaign_type') && (
                  <FormHelperText error>{getErrorMessage('campaign_type')}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {/* Agency Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={hasError('agency_id')}>
                <InputLabel id="agency-label">Agency</InputLabel>
                <Select
                  labelId="agency-label"
                  id="agency_id"
                  name="agency_id"
                  value={formData.agency_id}
                  onChange={handleChange}
                  label="Agency"
                  disabled={loading}
                >
                  <MenuItem value="">None</MenuItem>
                  {agencies.map((agency) => (
                    <MenuItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </MenuItem>
                  ))}
                </Select>
                {hasError('agency_id') && (
                  <FormHelperText error>{getErrorMessage('agency_id')}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {/* Objective */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="objective"
                name="objective"
                label="Objective"
                value={formData.objective}
                onChange={handleChange}
                multiline
                rows={2}
                error={hasError('objective')}
                helperText={getErrorMessage('objective')}
              />
            </Grid>
            
            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date *"
                  value={formData.start_date}
                  onChange={(date) => handleDateChange('start_date', date)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      error={hasError('start_date')}
                      helperText={getErrorMessage('start_date')}
                    />
                  )}
                  format="yyyy-MM-dd"
                />
              </LocalizationProvider>
            </Grid>
            
            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date *"
                  value={formData.end_date}
                  onChange={(date) => handleDateChange('end_date', date)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      error={hasError('end_date')}
                      helperText={getErrorMessage('end_date')}
                    />
                  )}
                  format="yyyy-MM-dd"
                  minDate={formData.start_date}
                />
              </LocalizationProvider>
            </Grid>
            

            
            {/* Media Types */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="media-types"
                options={mediaTypes}
                value={formData.media_types}
                onChange={handleMediaTypesChange}
                disabled={loading}
                isOptionEqualToValue={(option, value) => {
                  // If both are strings, do a direct comparison
                  if (typeof option === 'string' && typeof value === 'string') {
                    return option.toLowerCase() === value.toLowerCase();
                  }
                  
                  // Extract comparable values
                  const getOptionText = (item) => {
                    if (typeof item === 'string') return item;
                    if (typeof item === 'object' && item !== null) {
                      const name = item.name || item.media_type || item.media_type_channel_name;
                      if (typeof name === 'object' && name !== null) {
                        return name.name || name.media_type || name.media_type_channel_name || 'Digital';
                      }
                      return name || 'Digital';
                    }
                    return 'Digital';
                  };
                  
                  // Compare normalized text values
                  const optionText = getOptionText(option);
                  const valueText = getOptionText(value);
                  
                  // Case-insensitive comparison
                  return typeof optionText === 'string' && typeof valueText === 'string' && 
                         optionText.toLowerCase() === valueText.toLowerCase();
                }}
                getOptionLabel={(option) => {
                  // If option is a string, use it directly
                  if (typeof option === 'string') return option;
                  
                  // If option is an object, extract the name from it
                  if (typeof option === 'object' && option !== null) {
                    const name = option.name || option.media_type || option.media_type_channel_name;
                    
                    // Handle nested objects
                    if (typeof name === 'object' && name !== null) {
                      return name.name || name.media_type || name.media_type_channel_name || 'Digital';
                    }
                    
                    return typeof name === 'string' ? name : 'Digital';
                  }
                  
                  // Fallback
                  return 'Digital';
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    // Extract key from props to avoid React warning
                    const { key, ...otherProps } = tagProps;
                    
                    // Extract the display text
                    let displayText;
                    if (typeof option === 'string') {
                      displayText = option;
                    } else if (typeof option === 'object' && option !== null) {
                      const name = option.name || option.media_type || option.media_type_channel_name;
                      
                      // Handle nested objects
                      if (typeof name === 'object' && name !== null) {
                        displayText = name.name || name.media_type || name.media_type_channel_name || 'Digital';
                      } else {
                        displayText = typeof name === 'string' ? name : 'Digital';
                      }
                    } else {
                      displayText = 'Digital';
                    }
                    
                    return (
                      <Chip
                        key={key}
                        label={displayText}
                        {...otherProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Media Types *"
                    placeholder="Select Media Types"
                    required
                    error={hasError('media_types')}
                    helperText={getErrorMessage('media_types')}
                  />
                )}
              />
              <FormHelperText>
                Select all media types for this campaign
              </FormHelperText>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        p: 3,
        gap: 2
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.12)',
            color: '#666',
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': {
              borderColor: '#2e7d32',
              backgroundColor: 'rgba(46, 125, 50, 0.04)',
              color: '#2e7d32',
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          sx={{
            backgroundColor: '#2e7d32',
            color: 'white',
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(46, 125, 50, 0.25)',
            '&:hover': {
              backgroundColor: '#1b5e20',
              boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.38)',
              boxShadow: 'none',
            }
          }}
        >
          {initialData ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDialog; 